
import { useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";

import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent } from "@/shared/components/ui/card";

import type { CandidateListItem } from "@/modules/hrPortal/types";
import { getStatusText } from "@/modules/hrPortal/utils/candidateStatus";

interface ApplicantsTableProps {
  applications: CandidateListItem[];
  department?: string;
}

function getAllocationBadge(application: CandidateListItem) {
  const allocation = application.allocation;

  if (!allocation) {
    return {
      label: "Allocation unknown",
      className: "border-slate-200 bg-slate-100 text-slate-600",
    };
  }

  if (allocation.isBlacklisted || allocation.allocationStatus === "BLACKLISTED") {
    return {
      label: "Globally blacklisted",
      className: "border-red-200 bg-red-50 text-red-700",
    };
  }

  if (allocation.isAllocatedToAnotherTeam) {
    return {
      label: "Allocated to another team",
      className: "border-amber-200 bg-amber-50 text-amber-800",
    };
  }

  if (
    allocation.isAllocatedToCurrentTeam ||
    allocation.allocationStatus === "ALLOCATED"
  ) {
    return {
      label: "Allocated to your team",
      className: "border-blue-200 bg-blue-50 text-blue-700",
    };
  }

  return {
    label: "Available",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
}

export default function ApplicantsTable({
  applications,
  department,
}: ApplicantsTableProps) {
  const navigate = useNavigate();

  const handleCandidateClick = (applicationId: string) => {
    navigate(`/hr/candidates/${applicationId}`);
  };

  return (
    <Card className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <CardContent className="p-0">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1050px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-white text-slate-600">
                <th className="px-4 py-4 text-left font-semibold">
                  Candidate
                </th>
                <th className="px-4 py-4 text-left font-semibold">
                  Position
                </th>
                <th className="px-4 py-4 text-left font-semibold">
                  Department
                </th>
                <th className="px-4 py-4 text-left font-semibold">
                  ATS Score
                </th>
                <th className="px-4 py-4 text-left font-semibold">
                  Status
                </th>
                <th className="px-4 py-4 text-left font-semibold">
                  Allocation
                </th>
                <th className="px-4 py-4 text-left font-semibold">
                  Voice Call
                </th>
                <th className="px-4 py-4 text-left font-semibold">
                  Applied
                </th>
              </tr>
            </thead>

            <tbody>
              {applications?.length > 0 ? (
                applications.map((application) => {
                  const statusText = getStatusText(application.status);
                  const atsScore = application.matchAnalysis?.atsScore;
                  const allocationBadge = getAllocationBadge(application);

                  const appliedDate = application.createdAt
                    ? new Date(application.createdAt)
                    : null;

                  const formattedDate =
                    appliedDate &&
                    !Number.isNaN(appliedDate.getTime())
                      ? appliedDate.toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "N/A";

                  return (
                    <tr
                      key={application.id}
                      role="link"
                      tabIndex={0}
                      aria-label={`View candidate ${application.fullName || "details"}`}
                      onClick={() => handleCandidateClick(application.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleCandidateClick(application.id);
                        }
                      }}
                      className="cursor-pointer border-b border-slate-100 bg-white transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 last:border-b-0"
                    >
                      <td className="px-4 py-4">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-600">
                            {application.fullName
                              ?.trim()
                              .charAt(0)
                              .toUpperCase() || "?"}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900">
                              {application.fullName || "Unknown Candidate"}
                            </p>
                            <p className="mt-0.5 max-w-[180px] truncate text-xs text-slate-500">
                              {application.email || "N/A"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <p className="font-semibold text-slate-900">
                          {application.currentDesignation || "N/A"}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {application.currentCompany || "N/A"}
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        {department ? (
                          <Badge
                            variant="outline"
                            className="whitespace-nowrap rounded-full border-slate-200 font-normal text-slate-700"
                          >
                            {department}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">N/A</span>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <Badge
                          variant="secondary"
                          className="rounded-full bg-slate-100 font-medium text-slate-800"
                        >
                          {atsScore != null ? `${atsScore}%` : "N/A"}
                        </Badge>
                      </td>

                      <td className="px-4 py-4">
                        <Badge
                          variant="secondary"
                          className="max-w-[210px] whitespace-normal rounded-full bg-blue-50 text-left font-normal text-blue-700 hover:bg-blue-50"
                        >
                          {statusText}
                        </Badge>
                      </td>

                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <Badge
                            variant="outline"
                            className={`whitespace-normal rounded-full font-normal ${allocationBadge.className}`}
                          >
                            {allocationBadge.label}
                          </Badge>

                          {application.allocation?.allocationStartedAt && (
                            <p className="text-xs text-slate-500">
                              Since{" "}
                              {new Date(
                                application.allocation.allocationStartedAt
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </p>
                          )}

                          {application.allocation?.isBlacklisted &&
                            application.allocation.blacklistExpiresAt && (
                              <p className="text-xs text-red-600">
                                Expires{" "}
                                {new Date(
                                  application.allocation.blacklistExpiresAt
                                ).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </p>
                            )}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <Badge
                          variant="secondary"
                          className="gap-1.5 rounded-full bg-slate-100 font-normal text-slate-700"
                        >
                          <Phone className="h-3 w-3" />
                          Pending
                        </Badge>
                      </td>

                      <td className="whitespace-nowrap px-4 py-4 text-slate-600">
                        {formattedDate}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-sm text-slate-500"
                  >
                    No applicants found for this job.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
